import { insightsIntl } from "../strings/intl";
import type { LegacyTableColumn, LegacyTableRow } from "@wayflyer/flyui-dataviz";
import { type IntlShape, useIntl } from "react-intl";
import type { BaseSchemas } from "@wayflyer/api-types";
import * as React from "react";
import { useCallback } from "react";
import { useLoaderData } from "react-router";
import { useCustomerAppConfig } from "@wayflyer/undercarriage";

import { Dimensions, MetricTypes } from "../autogen/metrics";
import { metricDescription, metricTooltip } from "../strings/metric-messages";
import type { InsightsLoaderReturn } from "../loader";
import { InsightsSingleTableScreen, type InsightsSingleTableScreenProps } from "../single-table-screen";
import { getUrls } from "../urls";
import { type ColumnRename, getRowContent, toColumns, toTableRows } from "../utils/dashboard-output-helpers";
import { dateRangePresets, useDateAndTemp } from "../utils/date";
import { aggregateDataPoints, type DashboardOutput, getDashboardOutput } from "../utils/fetch-api";
import fetchDashboardMetaData, { type DashboardMetadata } from "../utils/fetch-dashboard-metadata";
import { getSort } from "../utils/metrics";

import {
  addExtraProductsData,
  getNewCustomerPercentMetric,
  getRefundsMetric,
  getReturnRateMetric,
  getTotalQuantityMetric,
  getTotalSalesMetric,
  useSalesVelocityData,
} from "./metrics";

const getToolTipForColumn = (intl: IntlShape) => (columnId: LegacyTableColumn["id"]) => {
  if (columnId === "Product") {
    return insightsIntl.formatMessage({
      id: "productsTab::Product",
      defaultMessage: "The type of product, disambiguated by name",
    });
  } else {
    return metricTooltip(intl, columnId);
  }
};

const summaryCache: Record<string, any> = {};

const dataToolTipMap = {
  Summary: "An overview of all the data - not broken down by product",
};

const TooFewProductsCutoffValue = 35;

const getSortRowsDefault = (metadata: DashboardMetadata) => (rowA, rowB, columnId, desc) => {
  const sortQuantity = getSort(getTotalQuantityMetric(metadata));

  // sort by raw data if found under the following id pattern.
  // otherwise sort by the data being displayed
  const sortIdStr = `sort_${columnId}`;
  const sortId = rowA.original[sortIdStr] ? sortIdStr : columnId;

  const specialSortMetrics = [
    MetricTypes.ITEM_LEVEL_RETURN_RATE_FROM_PRODUCTS,
    MetricTypes.ITEM_LEVEL_RETURN_RATE_FROM_REFUNDS,
    MetricTypes.ITEM_LEVEL_NEW_CUSTOMER_PERCENT_FROM_PRODUCTS,
    MetricTypes.ITEM_LEVEL_NEW_CUSTOMER_PERCENT,
  ];

  if (specialSortMetrics.indexOf(columnId) > -1) {
    if (
      rowA.original[sortQuantity] > TooFewProductsCutoffValue &&
      rowB.original[sortQuantity] <= TooFewProductsCutoffValue
    ) {
      return desc ? 1 : -1;
    }

    if (
      rowA.original[sortQuantity] <= TooFewProductsCutoffValue &&
      rowB.original[sortQuantity] > TooFewProductsCutoffValue
    ) {
      return desc ? -1 : 1;
    }
  }

  if (rowA.original[sortId] > rowB.original[sortId]) {
    return 1;
  }
  if (rowB.original[sortId] > rowA.original[sortId]) {
    return -1;
  }
  return 0;
};

const getOutputChartTabs = (intl: IntlShape, metadata: DashboardMetadata | undefined) => {
  if (!metadata) {
    return [];
  }

  const total_quantity_metric = getTotalQuantityMetric(metadata);
  const total_sales_metric = getTotalSalesMetric(metadata);
  const total_quantity_inc_refunds_metric = MetricTypes.GROSS_ITEM_QUANTITY;
  const total_sales_inc_refunds_metric = MetricTypes.GROSS_PRODUCT_REVENUE_FROM_ORDER_PROPORTION_LESS_DISCOUNTS;

  const returnsMetric = getRefundsMetric(metadata);
  const returnRateMetric = getReturnRateMetric(metadata);

  const tabs: ColumnRename[] = [
    {
      title: insightsIntl.formatMessage({ id: "Total Sales", defaultMessage: "Total Sales" }),
      metricType: total_sales_metric,
    },
    {
      title: insightsIntl.formatMessage({ id: "Net Quantity", defaultMessage: "Net Quantity" }),
      metricType: total_quantity_metric,
    },
  ];

  if (returnsMetric) {
    tabs.push({
      title: metricDescription(intl, MetricTypes.PRODUCT_REFUNDS_FROM_REFUNDS),
      metricType: returnsMetric,
    });
  }

  if (returnRateMetric) {
    tabs.push({
      title: insightsIntl.formatMessage({ id: "Return Rate", defaultMessage: "Return Rate" }),
      metricType: returnRateMetric,
    });
  }

  tabs.push(
    {
      title: insightsIntl.formatMessage({ id: "Ordered Quantity", defaultMessage: "Ordered Quantity" }),
      metricType: total_quantity_inc_refunds_metric,
    },
    {
      title: insightsIntl.formatMessage({ id: "Gross Revenue", defaultMessage: "Gross Revenue" }),
      metricType: total_sales_inc_refunds_metric,
    },
    {
      title: metricDescription(intl, MetricTypes.ITEM_LEVEL_NEW_CUSTOMER_PERCENT),
      metricType: getNewCustomerPercentMetric(metadata),
    },
    {
      title: metricDescription(intl, MetricTypes.AVERAGE_DAILY_UNITS),
      metricType: MetricTypes.AVERAGE_DAILY_UNITS,
    },
    {
      title: metricDescription(intl, MetricTypes.SALES_VELOCITY_THIRTY_DAYS),
      metricType: MetricTypes.SALES_VELOCITY_THIRTY_DAYS,
    },
    {
      title: metricDescription(intl, MetricTypes.PRODUCT_LEVEL_DISCOUNT_PCT),
      metricType: MetricTypes.PRODUCT_LEVEL_DISCOUNT_PCT,
    }
  );

  if (metadata.multiple_stores_connected) {
    tabs.push({
      title: metricDescription(intl, MetricTypes.STORE_NAME),
      metricType: MetricTypes.STORE_NAME,
    });
  }

  return tabs;
};

const INITIAL_PAGE_SIZE = 50;

const PRODUCTS_LOCAL_STORAGE_PATH = "wayflyer-insights-products-";

/**
 * Entry point
 */

const ProductsScreen = () => {
  const intl = useIntl();
  const { customerDomain }: InsightsLoaderReturn = useLoaderData();
  const { dashboardDataPath } = getUrls(customerDomain);
  const { data: appConfig } = useCustomerAppConfig();
  const enableStickyColumns = false; // Product column is pinned by default by InsightsDataTable
  const [errorProducts, setErrorProducts] = React.useState<string | undefined>();

  const [metaData, setMetaData] = React.useState<DashboardMetadata | undefined>();

  const {
    handleTimeRangeChange: handleDatePickerOnChange,
    selectedTimeRange,
    currentPreset,
    // eslint-disable-next-line react-hooks/immutability -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
  } = useDateAndTemp(() => setNeedsProcessData(true));

  const [processedData, setProcessedData] = React.useState<InsightsSingleTableScreenProps["tableProps"]>({
    columns: [],
    data: [],
  });
  const [summaryRow, setSummaryRow] = React.useState<LegacyTableRow | null>(null);

  const [hasFetchedInitialData, setHasFetchedInitialData] = React.useState(false);
  const [needsProcessData, setNeedsProcessData] = React.useState(false);

  const salesVelocityOutput = useSalesVelocityData(dashboardDataPath, metaData);

  const combineData = useCallback(() => {
    const allChartTabs = getOutputChartTabs(intl, metaData);

    getDashboardOutput(
      dashboardDataPath,
      [
        {
          metrics: allChartTabs.map((tab) => tab.metricType),
          filename: "products_insights",
          groupBys: [Dimensions.PRODUCT],
        },
      ],
      selectedTimeRange,
      (data) => {
        // at this point, we know that and salesVelocityOutput and metaData are not undefined, because of the other useEffect
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const augmentedData = addExtraProductsData(data, salesVelocityOutput!, metaData!, selectedTimeRange);
        const { table: processedData, summary } = generateOutputData(
          augmentedData,
          allChartTabs,
          // at this point, we know that metaData is not undefined
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          metaData!,
          appConfig,
          setSummaryRow,
          enableStickyColumns,
          intl
        );
        setProcessedData(processedData);
        setSummaryRow(summary);
        setHasFetchedInitialData(true);
      },
      Dimensions.PRODUCT,
      (error) => {
        setErrorProducts(error);
      }
    );
  }, [appConfig, enableStickyColumns, dashboardDataPath, metaData, salesVelocityOutput, selectedTimeRange, intl]);

  React.useEffect(() => {
    fetchDashboardMetaData(
      dashboardDataPath,
      (data) => {
        setMetaData(data);
        setNeedsProcessData(true);
      },
      (error) => setErrorProducts(error)
    );
  }, [dashboardDataPath]);

  React.useEffect(() => {
    if (needsProcessData && salesVelocityOutput) {
      setTimeout(combineData, 0);
      setNeedsProcessData(false);
    }
  }, [needsProcessData, combineData, salesVelocityOutput]);

  const hint = (
    <span>
      {insightsIntl.formatMessage({
        id: "Analyze your performance across all products.",
        defaultMessage: "Analyze your performance across all products.",
      })}
    </span>
  );

  return (
    <InsightsSingleTableScreen
      dataError={errorProducts}
      dataLoading={!hasFetchedInitialData}
      tableProps={{ ...processedData, summaryRow }}
      pageTitle={insightsIntl.formatMessage({ id: "performance products page title", defaultMessage: "Products" })}
      hint={hint}
      datePickerHeaderProps={{
        currentPreset,
        startDate: selectedTimeRange.start.format("YYYY-MM-DD"),
        endDate: selectedTimeRange.end.format("YYYY-MM-DD"),
        dateRangePresets: dateRangePresets,
        onChange: (dateRangePreset, startDate, endDate) =>
          handleDatePickerOnChange(
            dateRangePreset as Parameters<typeof handleDatePickerOnChange>[0],
            startDate,
            endDate
          ),
      }}
      datePickerOnTop={true}
    />
  );
};

const generateOutputData = (
  data: DashboardOutput,
  allChartTabs: ColumnRename[],
  metadata: DashboardMetadata,
  appConfig: BaseSchemas.CustomerAppConfig,
  setSummaryRow,
  enableStickyColumns: boolean,
  intl: IntlShape
): { table: InsightsSingleTableScreenProps["tableProps"]; summary: LegacyTableRow } => {
  // The backend has a * 100 in the dashboard generation, add this hack for now until we resolve
  const totalSalesMetric = getTotalSalesMetric(metadata);
  const returnRateMetric = getReturnRateMetric(metadata);
  const tableSettingsStoragePath = PRODUCTS_LOCAL_STORAGE_PATH + appConfig.customerDomain + "/";

  const percentMetrics = [getNewCustomerPercentMetric(metadata), MetricTypes.PRODUCT_LEVEL_DISCOUNT_PCT];
  if (returnRateMetric) {
    percentMetrics.push(returnRateMetric);
  }

  percentMetrics.forEach((metric) => {
    data.data.forEach((dataPoint) => {
      dataPoint[metric] = (dataPoint[metric] as number) * 100;
    });
    data.summary[metric] = (data.summary[metric] as number) * 100;
  });

  const { data: tableRows, summary } = toTableRows(data, Dimensions.PRODUCT);
  const columns = toColumns(data, Dimensions.PRODUCT, allChartTabs);

  const searchProps = {
    enableSearch: true,
    searchPlaceholder: insightsIntl.formatMessage({ id: "Search Products...", defaultMessage: "Search Products..." }),
    enableColumnSelector: true,
    columnSelectorLabel: insightsIntl.formatMessage({ id: "Table Settings", defaultMessage: "Table Settings" }),
    columnSelectorShowSearch: true,
    columnSelectorSearchPlaceholder: insightsIntl.formatMessage({
      id: "Search columns...",
      defaultMessage: "Search columns...",
    }),
  };

  columns.forEach((column) => {
    if (column.id === Dimensions.PRODUCT) {
      column.allowSelection = false;
    } else {
      column.disableSearch = true;
    }
    const hiddenMetrics = [
      MetricTypes.GROSS_ITEM_QUANTITY,
      MetricTypes.GROSS_PRODUCT_REVENUE_FROM_ORDER_PROPORTION_LESS_DISCOUNTS,
    ];
    if (hiddenMetrics.indexOf(column.id as MetricTypes) > -1) {
      column.selectedByDefault = false;
    }
  });

  const onSearchChangeHandler = (searchTerm: string, rows: LegacyTableRow[]) => {
    if (summaryCache[searchTerm]) {
      setSummaryRow(summaryCache[searchTerm]);
    }

    if (!rows.length) {
      summaryCache[searchTerm] = {};
      setSummaryRow({});
      return;
    }

    const dataPoints = rows.map((row) => {
      const rv = {};
      Object.keys(row).forEach((key) => {
        if (key.startsWith(getSort(""))) {
          rv[key.substring(getSort("").length)] = row[key];
        }
      });
      return rv;
    });

    const metrics = Object.keys(dataPoints[0]) as MetricTypes[];
    const summary = aggregateDataPoints(dataPoints, metrics, []);

    // Hack, remove one day
    percentMetrics.forEach((metric) => {
      summary[metric] = (summary[metric] as number) * 100;
    });

    const summaryRow = getRowContent(summary, data.currencyCode);
    summaryRow[Dimensions.PRODUCT] = "Summary";

    [MetricTypes.STORE_NAME].forEach((metricType) => {
      summaryRow[metricType] = "-";
    });

    summaryCache[searchTerm] = summaryRow;
    setSummaryRow(summaryRow);
  };
  if (enableStickyColumns) {
    for (const col in columns) {
      if (columns[col].id === Dimensions.PRODUCT) {
        columns[col].stickyParams = { align: "left" };
        break;
      }
    }
  }

  return {
    table: {
      columns,
      data: tableRows,
      initialPageSize: INITIAL_PAGE_SIZE,
      enablePagination: tableRows.length > INITIAL_PAGE_SIZE,
      sortBy: [{ id: totalSalesMetric, desc: true }],
      getToolTipForColumn: getToolTipForColumn(intl),
      getRowStyle: (record) => {
        return {
          fontWeight: record["_is_summary"] === 1 ? "bold" : "normal",
        };
      },
      getToolTipForData: (record, columnId) => {
        return dataToolTipMap[record[columnId]];
      },
      sortRows: getSortRowsDefault(metadata),
      ...searchProps,
      onSearchChangeHandler,
      tableSettingsStoragePath,
    },
    summary,
  };
};

export default ProductsScreen;
